import { ArrowRight, Info } from "lucide-react";
import type { ReactNode } from "react";
import { formatCount } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link, useInRouterContext } from "react-router-dom";

import { Section, type DiagnosticTabProps } from "../../DiagnosticSection";
import { usageProfile, type UsageProfileBucket } from "../../diagnosticData";
import { usageProfileLabel } from "./usageProfileLabel";

/* -------------------------------------------------------------------------- */
/* 2 · usuários por tipo da SAP (#873)                                         */
/* -------------------------------------------------------------------------- */


/**
 * "Ver usuários" (#1478): quem são os ativos, com papéis e SKU sugerido. Vem antes
 * das quantidades da BOM porque é a pergunta que o cliente faz primeiro.
 */
function UsersLink() {
  const { t } = useTranslation();
  if (!useInRouterContext()) return null;
  return (
    <Link
      to="active-users"
      data-testid="usage-profile-users-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See users")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/**
 * "Ver quantidades da BOM" (#1345, F3): o perfil dimensiona as licenças, e daqui
 * se vê quanto de cada SKU a BOM vigente tem, sem R$ (a revelação é do Ato 5).
 */
function BomQuantitiesLink() {
  const { t } = useTranslation();
  if (!useInRouterContext()) return null;
  return (
    <Link
      to="bom-quantities"
      data-testid="usage-profile-bom-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See the BOM quantities")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/**
 * Como a operação usa o SAP hoje, nos quatro baldes do desenho (#682).
 *
 * O card anterior mostrava o tipo técnico da conta (USTYP) e declarava no
 * rodapé que aquilo **não** era perfil de uso. Era verdade, e era o que havia:
 * a busca de então procurou um catálogo ligando a extração aos quatro nomes e
 * não achou nenhum, nem nosso nem da SAP.
 *
 * O perfil não é consultado, é derivado. O waterfall do SKU Mapper já
 * classifica cada usuário pelos tcodes que ele executa, pelos papéis que
 * carrega, pelos módulos e pelos scope items, e é esse mesmo resultado que
 * dimensiona a BOM do Ato 5 — que é precisamente o que a prosa do desenho
 * afirma sobre este card.
 */
/**
 * Cada perfil abre a lista de usuários ativos já filtrada por ele (#1478). Fora de
 * um roteador (anexo, testes do ato sem rota) a linha fica como era.
 */
function ProfileRowLink({ bucket, children }: { bucket: UsageProfileBucket; children: ReactNode }) {
  if (!useInRouterContext()) return <>{children}</>;
  return (
    <Link
      to={`active-users?profile=${bucket}`}
      className="block rounded-sm hover:bg-[#f7f9fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
    >
      {children}
    </Link>
  );
}

export function UsageProfileCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const profile = usageProfile(sections);
  const peak = Math.max(1, ...(profile?.rows ?? []).map((row) => row.count));
  const leader = profile?.rows[0];

  return (
    <Section
      sections={sections}
      name="user_sku_sample"
      title={t("Active users by usage profile")}
      subtitle={t("How the operation uses SAP today")}
      snapshots={snapshots}
      showsSampling={false}
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <UsersLink />
          <BomQuantitiesLink />
        </div>
      }
    >
      {profile && (
        <>
          <div data-testid="usage-profile" className="flex flex-col gap-2">
            {profile.rows.map((row) => (
              <ProfileRowLink key={row.bucket} bucket={row.bucket}>
              <div
                data-testid={`usage-profile-${row.bucket}`}
                className="flex items-center gap-3 text-[12px]"
              >
                <div className="w-36 shrink-0 truncate text-[#08254c]">
                  {usageProfileLabel(row.bucket, t)}
                </div>
                <div className="h-4 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
                  <div
                    className="h-full rounded-sm bg-[#6d8fc4]"
                    style={{ width: `${Math.max(1, (row.count / peak) * 100)}%` }}
                  />
                </div>
                <div className="w-16 shrink-0 text-right font-mono tabular-nums text-[#08254c]">
                  {formatCount(row.count)}
                </div>
              </div>
              </ProfileRowLink>
            ))}
          </div>

          {leader && leader.share != null && (
            <p
              data-testid="usage-profile-insight"
              className="mt-3 text-[12px] leading-snug text-[#596b85]"
            >
              {t(
                "The {{profile}} profile accounts for {{percent}} of the base — it is what sizes the licences in Investment.",
                {
                  profile: usageProfileLabel(leader.bucket, t).toLocaleLowerCase(),
                  percent: `${Math.round(leader.share * 100)}%`,
                },
              )}
            </p>
          )}

          {profile.unmapped > 0 && (
            <p
              data-testid="usage-profile-unmapped"
              className="mt-2 flex items-start gap-2 rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[11px] leading-snug text-[#596b85]"
            >
              <Info aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-[#7b8799]" />
              <span>
                {t(
                  "{{count}} users were classified into a licence type that falls into none of the four profiles.",
                  { count: profile.unmapped },
                )}
              </span>
            </p>
          )}
        </>
      )}
    </Section>
  );
}
